import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Repli personnalisé ; sinon une carte d'erreur générique avec bouton « Réessayer ». */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Capture les erreurs de rendu d'une sous-arborescence et affiche un repli, au lieu de laisser
 * planter toute la page (écran blanc) — typiquement utile autour du rendu Markdown.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("Erreur de rendu capturée par ErrorBoundary:", error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) {
        return this.props.fallback(error, this.reset);
      }
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <Stack spacing={2} alignItems="center" sx={{ maxWidth: 480, textAlign: "center" }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 48 }} />
            <Typography variant="h6">Une erreur est survenue</Typography>
            <Typography variant="body2" color="text.secondary">
              {error.message || "Le contenu n'a pas pu s'afficher."}
            </Typography>
            <Button variant="contained" onClick={this.reset}>
              Réessayer
            </Button>
          </Stack>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
